import DualPanelSelectorController from "./dual_panel_selector_controller"

/**
 * ParticipantSelectorController - Sélection de participants
 *
 * Hérite de DualPanelSelectorController avec:
 * - Filtres par client et promotion (dropdown autocomplete)
 * - Affichage du client associé
 */
export default class extends DualPanelSelectorController {
  static targets = [
    ...DualPanelSelectorController.targets,
    "customerFilter",
    "customerOptions",
    "promotionFilter",
    "promotionOptions"
  ]

  static values = {
    ...DualPanelSelectorController.values,
    customersUrl: String,
    promotionsUrl: String,
    createModalUrl: String,
    selectedCustomerId: Number,
    selectedPromotionId: Number,
    inputPrefix: { type: String, default: "promotion[enrollments_attributes]" },
    existingEnrollments: { type: Array, default: [] }
  }

  connect() {
    super.connect()
    this.initFilters()
  }

  // ==================== Filtres ====================

  initFilters() {
    // Les dropdowns sont gérés via les events des sous-elements
  }

  async onCustomerFilterInput(event) {
    const query = event.target.value.trim()
    if (query.length < 1) {
      this.hideCustomerOptions()
      return
    }

    try {
      const response = await fetch(`${this.customersUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken }
      })
      const data = await response.json()
      this.renderCustomerOptions(data.customers)
    } catch (error) {
      console.error("Erreur filtrage clients:", error)
    }
  }

  renderCustomerOptions(customers) {
    if (!this.hasCustomerOptionsTarget) return

    if (customers.length === 0) {
      this.customerOptionsTarget.innerHTML = `
        <div class="p-3 text-sm text-gray-500 text-center">Aucun client trouvé</div>
      `
    } else {
      this.customerOptionsTarget.innerHTML = customers.map(c => `
        <button type="button"
                class="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                data-action="click->participant-selector#selectCustomer"
                data-customer-id="${c.id}"
                data-customer-name="${this.escapeHtml(c.name)}">
          ${this.escapeHtml(c.name)}
        </button>
      `).join("")
    }

    this.customerOptionsTarget.classList.remove("hidden")
  }

  selectCustomer(event) {
    const { customerId, customerName } = event.currentTarget.dataset

    this.selectedCustomerIdValue = parseInt(customerId)
    this.customerFilterTarget.value = customerName

    this.hideCustomerOptions()
    this.currentPageValue = 1
    this.performSearch()
  }

  clearCustomerFilter() {
    this.selectedCustomerIdValue = 0
    this.customerFilterTarget.value = ""
    this.currentPageValue = 1
    this.performSearch()
  }

  hideCustomerOptions() {
    if (this.hasCustomerOptionsTarget) {
      this.customerOptionsTarget.classList.add("hidden")
    }
  }

  async onPromotionFilterInput(event) {
    const query = event.target.value.trim()
    if (query.length < 1) {
      this.hidePromotionOptions()
      return
    }

    try {
      const response = await fetch(`${this.promotionsUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken }
      })
      const data = await response.json()
      this.renderPromotionOptions(data.promotions)
    } catch (error) {
      console.error("Erreur filtrage promotions:", error)
    }
  }

  renderPromotionOptions(promotions) {
    if (!this.hasPromotionOptionsTarget) return

    if (promotions.length === 0) {
      this.promotionOptionsTarget.innerHTML = `
        <div class="p-3 text-sm text-gray-500 text-center">Aucune promotion trouvée</div>
      `
    } else {
      this.promotionOptionsTarget.innerHTML = promotions.map(p => `
        <button type="button"
                class="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                data-action="click->participant-selector#selectPromotion"
                data-promotion-id="${p.id}"
                data-promotion-name="${this.escapeHtml(p.name)}">
          <span class="font-medium">${this.escapeHtml(p.name)}</span>
          ${p.course_name ? `<span class="text-gray-500 text-xs ml-2">${this.escapeHtml(p.course_name)}</span>` : ""}
        </button>
      `).join("")
    }

    this.promotionOptionsTarget.classList.remove("hidden")
  }

  selectPromotion(event) {
    const { promotionId, promotionName } = event.currentTarget.dataset

    this.selectedPromotionIdValue = parseInt(promotionId)
    this.promotionFilterTarget.value = promotionName

    this.hidePromotionOptions()
    this.currentPageValue = 1
    this.performSearch()
  }

  clearPromotionFilter() {
    this.selectedPromotionIdValue = 0
    this.promotionFilterTarget.value = ""
    this.currentPageValue = 1
    this.performSearch()
  }

  hidePromotionOptions() {
    if (this.hasPromotionOptionsTarget) {
      this.promotionOptionsTarget.classList.add("hidden")
    }
  }

  // ==================== Override search ====================

  async performSearch() {
    const query = this.hasSearchTarget ? this.searchTarget.value.trim() : ""

    this.showLoading()

    try {
      const params = new URLSearchParams({
        q: query,
        page: this.currentPageValue,
        per_page: this.itemsPerPageValue
      })

      // Ajouter les filtres
      if (this.selectedCustomerIdValue) {
        params.append("customer_id", this.selectedCustomerIdValue)
      }
      if (this.selectedPromotionIdValue) {
        params.append("promotion_id", this.selectedPromotionIdValue)
      }

      // Exclure les IDs déjà sélectionnés
      this.selectedIdsValue.forEach(id => {
        params.append("exclude_ids[]", id)
      })

      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const data = await response.json()
      this.handleSearchResponse(data)
    } catch (error) {
      console.error("Erreur recherche:", error)
      this.announce("Erreur lors de la recherche")
    } finally {
      this.hideLoading()
    }
  }

  // ==================== API Response ====================

  getItemsFromResponse(data) {
    return data.students || []
  }

  // ==================== Rendu disponibles ====================

  renderAvailableItemHTML(item) {
    const customerBadge = item.customer_name
      ? `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
           ${this.escapeHtml(item.customer_name)}
         </span>`
      : ""

    return `
      <button type="button"
              class="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 flex items-center gap-3"
              data-item-id="${item.id}"
              data-item-name="${this.escapeHtml(item.name)}"
              data-item-email="${this.escapeHtml(item.email)}"
              data-item-customer-id="${item.customer_id || ""}"
              data-item-customer-name="${this.escapeHtml(item.customer_name || "")}"
              data-action="click->participant-selector#toggle"
              role="option"
              aria-selected="false"
              tabindex="0">
        <div class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium text-sm flex-shrink-0" aria-hidden="true">
          ${this.getInitials(item.name)}
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-900">${this.escapeHtml(item.name)}</span>
            ${customerBadge}
          </div>
          <p class="text-sm text-gray-500 truncate">${this.escapeHtml(item.email)}</p>
        </div>
        <i class="fa-solid fa-plus text-gray-400 flex-shrink-0" aria-hidden="true"></i>
      </button>
    `
  }

  // ==================== Rendu sélectionnés ====================

  renderSelectedItemHTML(item) {
    const customerBadge = item.customer_name
      ? `<span class="text-xs text-gray-500 block">${this.escapeHtml(item.customer_name)}</span>`
      : ""

    return `
      <div class="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
           data-item-id="${item.id}"
           role="option"
           aria-selected="true"
           tabindex="0">
        <div class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium text-xs flex-shrink-0" aria-hidden="true">
          ${this.getInitials(item.name)}
        </div>
        <div class="flex-1 min-w-0">
          <span class="font-medium text-gray-900 text-sm">${this.escapeHtml(item.name)}</span>
          ${customerBadge}
        </div>
        <button type="button"
                class="text-red-400 hover:text-red-600 transition-colors p-1"
                data-action="click->participant-selector#toggle"
                aria-label="Retirer ${this.escapeHtml(item.name)}"
                tabindex="-1">
          <i class="fa-solid fa-times text-xs" aria-hidden="true"></i>
        </button>
      </div>
    `
  }

  // ==================== Extraction données ====================

  extractItemData(element) {
    return {
      id: parseInt(element.dataset.itemId),
      name: element.dataset.itemName || "",
      email: element.dataset.itemEmail || "",
      customer_id: element.dataset.itemCustomerId ? parseInt(element.dataset.itemCustomerId) : null,
      customer_name: element.dataset.itemCustomerName || ""
    }
  }

  // ==================== Hidden inputs ====================

  renderHiddenInputsForItem(item, index) {
    const prefix = this.inputPrefixValue
    const existingEnrollment = this.findExistingEnrollment(item.id)

    if (existingEnrollment) {
      // Use numeric index for existing enrollments - Rails strong params require numeric keys
      return `
        <input type="hidden" name="${prefix}[${index}][id]" value="${existingEnrollment.id}">
        <input type="hidden" name="${prefix}[${index}][user_id]" value="${item.id}">
      `
    }

    // Use numeric index for new enrollments - Rails strong params require numeric keys
    return `
      <input type="hidden" name="${prefix}[${index}][user_id]" value="${item.id}">
    `
  }

  updateHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return

    let html = ""
    const prefix = this.inputPrefixValue
    let inputIndex = 0

    // Generate inputs for selected items
    this.selectedItemsValue.forEach((item) => {
      html += this.renderHiddenInputsForItem(item, inputIndex)
      inputIndex++
    })

    // Generate _destroy inputs for removed enrollments
    const removedEnrollments = this.getRemovedEnrollments()
    removedEnrollments.forEach((enrollment) => {
      html += `
        <input type="hidden" name="${prefix}[${inputIndex}][id]" value="${enrollment.id}">
        <input type="hidden" name="${prefix}[${inputIndex}][_destroy]" value="true">
      `
      inputIndex++
    })

    this.hiddenInputsTarget.innerHTML = html
  }

  findExistingEnrollment(userId) {
    return this.existingEnrollmentsValue.find(e => e.user_id === userId)
  }

  getRemovedEnrollments() {
    const selectedUserIds = this.selectedIdsValue
    return this.existingEnrollmentsValue.filter(e => !selectedUserIds.includes(e.user_id))
  }

  // ==================== Création inline ====================

  // Appelé après création réussie via Turbo Stream (event-dispatch)
  handleParticipantCreated(event) {
    const { id, name, email, customer_id, customer_name } = event.detail

    const newItem = { id, name, email, customer_id, customer_name: customer_name || "" }
    this.selectedIdsValue = [...this.selectedIdsValue, id]
    this.selectedItemsValue = [...this.selectedItemsValue, newItem]

    this.renderSelectedItem(newItem)
    this.updateCounts()
    this.updateHiddenInputs()
    this.performSearch()

    this.announce(`Participant "${name}" créé et ajouté`)
  }

  // ==================== Helpers ====================

  getInitials(name) {
    if (!name) return "?"
    const parts = name.split(" ")
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }
}
